import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { interpolateColor, useAnimatedRef, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import { useFocusEffect } from '@react-navigation/native';
import { AppView, rem, SCREEN_BOTTOM_PADDING } from 'design-system';
import Amount from 'design-system/Amount';
import { IconCoins } from 'design-system/icons';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import WebTabs from 'design-system/WebTabs.web';
import { useHeaderHeight, useRedemptions } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import RewardsScreenContent from 'features/rewards/components/RewardsScreenContent';
import { TAB_SCREEN_HEADER_HEIGHT } from 'navigators/styles';
import Amplitude from 'utils/amplitude';
import PotsScreen from './PotsScreen';
import SaveOpportunitiesScreen from './SaveOpportunitiesScreen';
const tabs = ['pots', 'cashback', 'opportunities'];
const displayTabs = ['Pots', 'Cashback', 'Opportunities'];
/**
 * Web version of the Save tab. The native tab pages between its sections with
 * a horizontal swipe; on web the sections sit behind `WebTabs`, as on the
 * other web tabs. Actions that open a pots account or move money are blocked
 * where they start, with `UnavailableOnWebModal`.
 */
// Same height as the other tabs' headers (Credit uses NavigationHeader's tab height). The
// native-stack type only declares backgroundColor, but the web header applies the height too
const HEADER_STYLE = { backgroundColor: 'transparent', height: TAB_SCREEN_HEADER_HEIGHT };
const SaveTab = ({ route, navigation }) => {
    const colors = useColors();
    const { redemptions } = useRedemptions();
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    const headerHeight = useHeaderHeight();
    // Scroll position of the visible section, which colours the header as on native
    const scrollY = useSharedValue(0);
    const headerBackgroundStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.background.dark,
            colors.cards.onDark,
        ]),
    }));
    const routeTab = route.params && 'tab' in route.params ? route.params.tab : undefined;
    const [activeTab, setActiveTab] = useState(routeTab ?? 'pots');
    useEffect(() => {
        if (routeTab) {
            setActiveTab(routeTab);
        }
    }, [routeTab]);
    // Each section opens at the top
    useEffect(() => {
        scrollY.value = 0;
    }, [activeTab, scrollY]);
    // Focus effect, as on native, so coming back to Save on Cashback logs it again
    useFocusEffect(useCallback(() => {
        if (activeTab === 'cashback') {
            Amplitude.logEvent('Rewards.Opened');
        }
    }, [activeTab]));
    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        navigation.setParams({ tab });
    }, [navigation]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerStyle: HEADER_STYLE,
            headerRight: activeTab === 'cashback'
                ? () => (<NavigationHeaderRightIconButton onPress={() => navigation.navigateDeprecated('ClaimRewards')} style={styles.headerRight}>
                <IconCoins color={colors.elements.primary}/>
                {redemptions.available?.currency ? (<Amount Numbers-14 value={{
                            amount: redemptions.available?.balance,
                            currency: redemptions.available?.currency,
                        }}/>) : null}
              </NavigationHeaderRightIconButton>)
                : undefined,
        });
    }, [
        activeTab,
        colors.elements.primary,
        colors.text.primary,
        navigation,
        redemptions.available?.balance,
        redemptions.available?.currency,
    ]);
    const potsScrollViewRef = useAnimatedRef();
    const opportunitiesScrollViewRef = useAnimatedRef();
    const potsScrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    const cashbackScrollY = useSharedValue(0);
    const cashbackScrollHandler = useAnimatedScrollHandler((event) => {
        cashbackScrollY.value = event.contentOffset.y;
        scrollY.value = event.contentOffset.y;
    });
    const onOpportunitiesScroll = useCallback((event) => {
        scrollY.value = event.nativeEvent.contentOffset.y;
    }, [scrollY]);
    return (<AppView withFrame={false} isInTab>
      {/* Sits under the transparent stack header, so the title and the tabs change colour together */}
      <Animated.View style={headerBackgroundStyle}>
        <View style={{ height: headerHeight }}/>
        <View style={paddingHorizontalStyle}>
          <WebTabs tabs={tabs} displayTabs={displayTabs} activeTab={activeTab} handleTabChange={handleTabChange}/>
        </View>
      </Animated.View>
      <View style={styles.content}>
        {activeTab === 'pots' && (<PotsScreen scrollViewRef={potsScrollViewRef} potsScrollHandler={potsScrollHandler} scrollViewExtraStyle={styles.scrollViewExtraStyle}/>)}
        {activeTab === 'cashback' && (<RewardsScreenContent isTabActive scrollY={cashbackScrollY} onScroll={cashbackScrollHandler}/>)}
        {activeTab === 'opportunities' && (<SaveOpportunitiesScreen scrollViewRef={opportunitiesScrollViewRef} onScroll={onOpportunitiesScroll} contentContainerStyle={[styles.opportunitiesContent, paddingHorizontalStyle]}/>)}
      </View>
    </AppView>);
};
const styles = StyleSheet.create({
    content: {
        flex: 1,
    },
    scrollViewExtraStyle: {
        paddingTop: rem(8),
    },
    opportunitiesContent: {
        paddingTop: rem(8),
        paddingBottom: SCREEN_BOTTOM_PADDING,
    },
    headerRight: {
        position: 'absolute',
        right: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(4),
        width: 'auto',
        paddingHorizontal: rem(16),
    },
});
export default SaveTab;
